/* ──────────────────────────────────────────────────────────────────────────
 * Layer catalogue (brief §1).
 *
 * Layers are configuration, not code. Adding a facility type is a new entry
 * here — the map, the layer panel and the search index all read from this one
 * list, so nothing else needs editing. That is what "configuration-driven
 * layers" in §15 means in practice.
 *
 * Every layer declares its classification, so RESTRICTED layers (police
 * posts, resource deployment) can be permission-gated in one place rather
 * than checked ad hoc at each render site.
 * ────────────────────────────────────────────────────────────────────────── */

import { CLASSIFICATION } from "../data/provenance";

/* Layer groups, in the order they appear in the left panel. Grouping matters:
 * 20 flat toggles is a wall; six groups of three or four is a control panel. */
export const LAYER_GROUPS = [
  { key: "zones",      label: { en: "Zones & Sectors",   mr: "झोन व सेक्टर" } },
  { key: "movement",   label: { en: "Movement",          mr: "हालचाल" } },
  { key: "transport",  label: { en: "Transport",         mr: "वाहतूक" } },
  { key: "safety",     label: { en: "Safety & Security", mr: "सुरक्षा" } },
  { key: "medical",    label: { en: "Medical",           mr: "वैद्यकीय" } },
  { key: "facilities", label: { en: "Public Facilities", mr: "सार्वजनिक सुविधा" } },
];

/* Geometry kinds the map knows how to render. */
export const GEOM = { POINT: "point", LINE: "line", POLYGON: "polygon" };

/* The catalogue.
 *
 * `defaultOn` is chosen so a first load reads as an operational picture
 * rather than a switchboard: zones, ghats and command posts on; the long tail
 * of facilities off until an operator asks for them (§7 — do not overload the
 * map with permanent markers).
 */
export const LAYERS = [
  // ── Zones & sectors ──────────────────────────────────────────────────
  { id: "sectors", group: "zones", geom: GEOM.POLYGON, defaultOn: true,
    label: { en: "Kumbh sectors / zones", mr: "कुंभ सेक्टर / झोन" },
    color: "#2563EB", icon: "Layers", classification: CLASSIFICATION.PUBLIC },

  { id: "ghats", group: "zones", geom: GEOM.POINT, defaultOn: true,
    label: { en: "Ghats", mr: "घाट" },
    color: "#0891B2", icon: "Waves", classification: CLASSIFICATION.PUBLIC },

  { id: "congregation", group: "zones", geom: GEOM.POLYGON, defaultOn: false,
    label: { en: "Congregation / event areas", mr: "मेळावा क्षेत्रे" },
    color: "#7C3AED", icon: "Users", classification: CLASSIFICATION.PUBLIC },

  // ── Movement ─────────────────────────────────────────────────────────
  { id: "entryGates", group: "movement", geom: GEOM.POINT, defaultOn: true,
    label: { en: "Entry gates", mr: "प्रवेशद्वार" },
    color: "#059669", icon: "LogIn", classification: CLASSIFICATION.PUBLIC },

  { id: "exitGates", group: "movement", geom: GEOM.POINT, defaultOn: true,
    label: { en: "Exit gates", mr: "निर्गमन द्वार" },
    color: "#DC2626", icon: "LogOut", classification: CLASSIFICATION.PUBLIC },

  { id: "pedestrianRoutes", group: "movement", geom: GEOM.LINE, defaultOn: false,
    label: { en: "Pedestrian routes", mr: "पादचारी मार्ग" },
    color: "#0D9488", icon: "Footprints", classification: CLASSIFICATION.PUBLIC },

  { id: "evacuationRoutes", group: "movement", geom: GEOM.LINE, defaultOn: false,
    label: { en: "Emergency evacuation routes", mr: "आपत्कालीन निर्वासन मार्ग" },
    color: "#EA580C", icon: "Siren", classification: CLASSIFICATION.INTERNAL },

  // ── Transport ────────────────────────────────────────────────────────
  { id: "vehicleRoutes", group: "transport", geom: GEOM.LINE, defaultOn: false,
    label: { en: "Vehicle routes", mr: "वाहन मार्ग" },
    color: "#475569", icon: "Navigation", classification: CLASSIFICATION.PUBLIC },

  { id: "diversionRoutes", group: "transport", geom: GEOM.LINE, defaultOn: false,
    label: { en: "Traffic diversion routes", mr: "वाहतूक वळण मार्ग" },
    color: "#D97706", icon: "GitBranch", classification: CLASSIFICATION.INTERNAL },

  { id: "parking", group: "transport", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Parking areas", mr: "वाहनतळ" },
    color: "#6366F1", icon: "SquareParking", classification: CLASSIFICATION.PUBLIC },

  { id: "transportPoints", group: "transport", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Bus / transport points", mr: "बस व वाहतूक केंद्रे" },
    color: "#8B5CF6", icon: "Bus", classification: CLASSIFICATION.PUBLIC },

  // ── Safety & security ────────────────────────────────────────────────
  // RESTRICTED: positions of police and command assets are operationally
  // sensitive. Gated by role, not merely hidden behind a toggle.
  { id: "policePosts", group: "safety", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Police / security posts", mr: "पोलीस व सुरक्षा चौक्या" },
    color: "#1D4ED8", icon: "Shield", classification: CLASSIFICATION.RESTRICTED },

  { id: "commandCentres", group: "safety", geom: GEOM.POINT, defaultOn: true,
    label: { en: "Command centres", mr: "कमांड सेंटर" },
    color: "#0F172A", icon: "Radio", classification: CLASSIFICATION.RESTRICTED },

  { id: "fireStations", group: "safety", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Fire / emergency stations", mr: "अग्निशमन केंद्रे" },
    color: "#B91C1C", icon: "Flame", classification: CLASSIFICATION.INTERNAL },

  // ── Medical ──────────────────────────────────────────────────────────
  { id: "hospitals", group: "medical", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Hospitals", mr: "रुग्णालये" },
    color: "#E11D48", icon: "Hospital", classification: CLASSIFICATION.PUBLIC },

  { id: "medicalCamps", group: "medical", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Medical camps", mr: "वैद्यकीय शिबिरे" },
    color: "#F43F5E", icon: "HeartPulse", classification: CLASSIFICATION.PUBLIC },

  { id: "ambulancePoints", group: "medical", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Ambulance points", mr: "रुग्णवाहिका केंद्रे" },
    color: "#FB7185", icon: "Ambulance", classification: CLASSIFICATION.INTERNAL },

  // ── Public facilities ────────────────────────────────────────────────
  { id: "toilets", group: "facilities", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Toilets", mr: "स्वच्छतागृहे" },
    color: "#64748B", icon: "Toilet", classification: CLASSIFICATION.PUBLIC },

  { id: "waterPoints", group: "facilities", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Drinking-water points", mr: "पिण्याच्या पाण्याची केंद्रे" },
    color: "#0EA5E9", icon: "Droplets", classification: CLASSIFICATION.PUBLIC },

  { id: "helpCentres", group: "facilities", geom: GEOM.POINT, defaultOn: false,
    label: { en: "Lost & found / help centres", mr: "हरवले-सापडले व मदत केंद्रे" },
    color: "#CA8A04", icon: "UserSearch", classification: CLASSIFICATION.PUBLIC },
];

/* Overlays are not facility layers — they are analytical products computed
 * over operational data, so they live apart from the catalogue and have their
 * own controls (§2). */
export const OVERLAYS = [
  { id: "crowdHeat", defaultOn: true,
    label: { en: "Crowd density heatmap", mr: "गर्दी घनता हीटमॅप" } },
  { id: "zoneStatus", defaultOn: true,
    label: { en: "Zone status (G/A/R)", mr: "झोन स्थिती (हिरवा/अंबर/लाल)" } },
  { id: "flowArrows", defaultOn: false,
    label: { en: "Pedestrian flow direction", mr: "पादचारी प्रवाह दिशा" } },
  { id: "incidents", defaultOn: true,
    label: { en: "Active incidents", mr: "सक्रिय घटना" } },
];

export const LAYER_BY_ID = Object.fromEntries(LAYERS.map((l) => [l.id, l]));

/* Initial toggle state, derived from the catalogue so the two cannot drift. */
export function defaultLayerState() {
  return Object.fromEntries(LAYERS.map((l) => [l.id, l.defaultOn]));
}
export function defaultOverlayState() {
  return Object.fromEntries(OVERLAYS.map((o) => [o.id, o.defaultOn]));
}

/* Which layers a given officer level may see.
 *
 * IMPORTANT (§11): this is a UI affordance, not a security control. The data
 * is in the client bundle; anyone can read it in devtools. Real enforcement
 * has to happen server-side, filtering RESTRICTED features out of the API
 * response before they ever reach the browser. This function exists so that
 * the permission *model* is in place for that backend to implement, and so
 * the UI does not casually display sensitive layers to every role.
 */
export function visibleLayersForRole(level) {
  const isSenior = level === "L1" || level === "L2";
  return LAYERS.filter((l) =>
    l.classification === CLASSIFICATION.RESTRICTED ? isSenior : true
  );
}
